'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BN, BigNum, QUOTE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { WALLET_BALANCES_QUERY_KEY } from './walletBalancesQueryKeys';
import { WalletBalance } from './types';
import { useCallback } from 'react';

const DEFAULT_BALANCE = (marketPrecisionExp?: BN): WalletBalance => ({
	base: BigNum.zero(marketPrecisionExp),
	quote: BigNum.zero(QUOTE_PRECISION_EXP),
});

const EMPTY_MAP = new Map<string, WalletBalance>();

/**
 * Hook to get wallet balance for a specific symbol.
 * Uses `select` to pick a single balance from the Map,
 * so the component only re-renders when that symbol's data changes.
 */
export function useWalletBalance(
	symbol: string | undefined,
	marketPrecisionExp?: BN
): WalletBalance {
	const select = useCallback(
		(data: Map<string, WalletBalance>) =>
			symbol ? data.get(symbol) : undefined,
		[symbol]
	);

	const { data } = useQuery<
		Map<string, WalletBalance>,
		Error,
		WalletBalance | undefined
	>({
		queryKey: WALLET_BALANCES_QUERY_KEY,
		queryFn: () => new Map(),
		enabled: false,
		staleTime: Infinity,
		select,
	});

	return data ?? DEFAULT_BALANCE(marketPrecisionExp);
}

/**
 * Hook to get all wallet balances from React Query cache.
 * Returns a Map of symbol -> WalletBalance.
 */
export function useAllWalletBalances(): Map<string, WalletBalance> {
	const { data } = useQuery<Map<string, WalletBalance>>({
		queryKey: WALLET_BALANCES_QUERY_KEY,
		queryFn: () => new Map(),
		enabled: false,
		staleTime: Infinity,
	});

	return data ?? EMPTY_MAP;
}

/**
 * Hook to set wallet balance data in the query cache.
 * Used by useSyncWalletBalances to update the cache.
 */
export function useSetWalletBalance() {
	const queryClient = useQueryClient();

	const setWalletBalance = useCallback(
		(symbol: string, balance: WalletBalance) => {
			queryClient.setQueryData<Map<string, WalletBalance>>(
				WALLET_BALANCES_QUERY_KEY,
				(prev) => {
					const newMap = new Map(prev);
					newMap.set(symbol, balance);
					return newMap;
				}
			);
		},
		[queryClient]
	);

	return setWalletBalance;
}
